<template>
  <div class="input">
    <input
      class="engine"
      type="text"
      v-model="search"
      @input="onChange"
      @keydown.down.prevent="onArrowDown"
      @keydown.up.prevent="onArrowUp"
      @keydown.enter="setResult()"
      @keydown.esc="cleanSearch"
      :placeholder="placeholder"
    />
    <ul v-if="inputVisibility" class="autocomplete-results">
      <li v-if="stateFound === 'not found' || stateFound === 'invalid'" class="autocomplete-result">
        No results found.
      </li>
      <li
        v-else
        v-for="(result, idx) in results"
        :key="idx"
        @click="setResult(result)"
        class="autocomplete-result"
        :class="{ 'is-active': idx === arrowCounter }"
      >
        <span v-html="result.displayHTML"></span>
      </li>
    </ul>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "Engine",
  data() {
    return {
      search: "",
      // Results will be an array of objects with id, type, and displayHTML
      results: [],
      arrowCounter: -1,
      stateFound: "",
      placeholder: "Search by gene, transcript, protein or variant (GRCh37/hg19)",
      isServerDown: false,
    };
  },
  computed: {
    // This computed property correctly determines if the dropdown should be visible
    inputVisibility() {
      // Show dropdown if there is a search term and we have results OR we have a "not found" message
      return this.search.length > 0 && (this.results.length > 0 || this.stateFound === 'not found' || this.stateFound === 'invalid');
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  destroyed() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    // Creates a pre-configured Axios instance for API calls
    axiosHandler() {
      const axiosInstance = axios.create({
        baseURL: process.env.VUE_APP_ROOT_API,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('ages_hms_access_token')}`,
        },
      });
      return axiosInstance;
    },
    serverDownError() {
      Swal.fire({
        title: '<p style="font-family:Ubuntu">Server Error<p>',
        html: '<p style="font-family:Ubuntu; font-size: 16px;">The server is currently down. If you’re seeing this page, please contact us via email.</p>',
        icon: 'error',
        confirmButtonText: 'Okay',
        iconColor: '#A51C30',
        customClass: {
          title: 'swal-title',
          text: 'swal-text',
          confirmButton: 'swal-confirm-button',
        }
      });
    },
    // Closes the autocomplete dropdown if a click occurs outside the component
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.cleanSearch();
      }
    },
    // Resets the search state
    cleanSearch() {
      this.search = "";
      this.results = [];
      this.arrowCounter = -1;
      this.stateFound = "";
    },
    // Formats the search string for the API query
    getFormatSearch(query) {
      let search = query.trim().replace(/[-:]/g, "_");
      search = search.replace(/\s/g, "");
      if (search.startsWith("r") || search.startsWith("R")) {
        return search.toLowerCase();
      }
      return search.toUpperCase();
    },
    
    /**
     * @description This is the core method that fetches and processes search results.
     * It now includes logic to highlight the matching part of the text and prioritize
     * displaying a matching name over an ID.
     */
    async onChange() {
      if (!this.search || this.search.trim() === "") {
        this.cleanSearch();
        return;
      }

      const originalSearch = this.search.trim();
      const formattedSearch = this.getFormatSearch(originalSearch);

      try {
        const response = await this.axiosHandler().post(`/api/search-id-rsid`, {
          prefix: formattedSearch,
        });
        
        // --- Success ---
        // Server is up, reset flag and close any error modals
        this.isServerDown = false; 
        if (Swal.isVisible()) Swal.close(); 

        const { flag, id_rsid, type } = response.data;
        this.stateFound = flag;

        if (flag === 'not found' || flag === 'invalid' || !id_rsid) {
          this.results = [];
          return;
        }

        const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const searchRegex = new RegExp(`(${escapeRegExp(originalSearch)})`, 'i');

        this.results = id_rsid.map(item => {
          const id = item[0];
          const names = item[1] || [];
          let displayBase = id; 
          const matchingName = names.find(name => name.toLowerCase().includes(originalSearch.toLowerCase()));
          if (matchingName) {
            displayBase = matchingName;
          }
          const displayHTML = displayBase.replace(searchRegex, `<strong>$1</strong>`);
          return { id, type, displayHTML };
        });
        
        this.arrowCounter = this.results.length > 0 ? 0 : -1;

      } catch (error) {
        // --- Failure ---
        console.error("Error during search:", error);
        this.results = []; // Always clear results on failure

        // Check if it's a server error (no response or 5xx status)
        if (!error.response || (error.response.status >= 500 && error.response.status <= 599)) {
          // --- This is a Server Error ---
          // By setting stateFound to "", the inputVisibility computed property
          // will return false, hiding the autocomplete dropdown.
          this.stateFound = ""; 
          
          if (!this.isServerDown) { // Only show modal once
            this.isServerDown = true;
            this.serverDownError();
          }
        } else {
          // --- This is a different error (e.g., "not found" from API) ---
          // Show "No results found." in the dropdown
          this.stateFound = "invalid"; 
        }
      }
    },
    
    /**
     * @description Navigates to the appropriate page based on the selected result's type.
     */
    setResult(result) {
      // If Enter is pressed, use the currently highlighted item
      if (!result && this.arrowCounter !== -1 && this.results[this.arrowCounter]) {
        result = this.results[this.arrowCounter];
      }
      
      if (!result) return; // Exit if there's still no result

      // --- CORE ROUTING LOGIC ---
      // Navigate based on whether the result is a 'gene' or a 'variant'
      if (result.type === 'gene') {
        this.$router.push({ name: 'RegionViewer', params: { geneId: result.id } });
      } else { // Assumes 'variant'
        this.$router.push({ name: 'Charts', params: { id: result.id } });
      }
      
      this.cleanSearch();
    },

    // Handles keyboard navigation down through the results list
    onArrowDown() {
      if (!this.inputVisibility || this.results.length === 0) return;
      if (this.arrowCounter < this.results.length - 1) {
        this.arrowCounter++;
      } else {
        this.arrowCounter = 0; // Loop to the top
      }
    },

    // Handles keyboard navigation up through the results list
    onArrowUp() {
      if (!this.inputVisibility || this.results.length === 0) return;
      if (this.arrowCounter > 0) {
        this.arrowCounter--;
      } else {
        this.arrowCounter = this.results.length - 1; // Loop to the bottom
      }
    },
  },
};
</script>

<style>
.input {
  width: 945px;
  display: flex;
  justify-content: center;
}

.engine input {
  width: 945px;
  outline: none;
  border: 1.5px solid #A51C30;
  border-radius: 5px;
  padding: 9px 8px;
  font-family: 'Ubuntu';
  font-size: 0.9rem;
  transition: 300ms;
}

.engine-small input {
  width: 370px;
  display: flex;
  justify-content: left !important;
  outline: none;
  border: 1.5px solid #A51C30;
  border-radius: 5px;
  padding: 9px 8px;
  height: 35px;
  font-family: 'Ubuntu';
  font-size: 12px;
  transition: 300ms;
  margin-left: 6px;

}

.engine-small:after {
  background-color: #A51C30;
  padding-top: 10px;
  padding-left: 14px;
  padding-right: 9px;
  color: white;
  position: relative;
  right: 6px;
  top: 0px;
  height: 23px;
  z-index: 1;
  width: 60px;
  font-size: 12px;
  border: 1px solid #A51C30;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
  content: "Search ID";
}

/* .engine:hover input {
  box-shadow: 0 0 15px rgba(10, 66, 221, 0.3);
  transition: 300ms;
} */
.engine:after {
  background-color: #A51C30;
  padding-top: 10px;
  padding-left: 15px;
  padding-right: 9px;
  color: white;
  position: relative;
  right: 3px;
  top: 0px;
  height: 31px;
  z-index: 1;
  width: 90px;
  border: 1px solid #A51C30;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
  content: "Search ID";
  font-size: 17px;
}

.autocomplete-results {
  background-color: white;
  z-index: 999999;
  max-height: 150px;
  overflow-y: scroll;
  position: absolute;
  width: 940px;
  border: 1.2px solid #a51c31aa;
  padding: 5px 2px;
  margin-top: 50px;
  border-radius: 5px;
  border-top-left-radius: 2px;
  border-top-right-radius: 2px;
}
.autocomplete-result {
  z-index: 99999;
  word-wrap: break-word;
  list-style-type: none;
  padding: 5px;
  font-size: 0.8rem;
  border-radius: 5px;
  font-size: 0.93rem;
  cursor: pointer;
  margin: 3px;
}
.engine-small > .autocomplete-results {
  background-color: white;
  z-index: 99999;
  width: 447px;
  border: 1.2px solid #a51c31aa;
  padding: 5px;
  margin-top: 38px;
  border-radius: 4px;
}
.engine-small .autocomplete-results > .autocomplete-result {
  z-index: 99999;
  word-wrap: break-word;
  list-style-type: none;
  padding: 5px;
  border-radius: 5px;
  font-size: 0.7rem;
  cursor: pointer;
}
.autocomplete-result:hover,
.autocomplete-result.is-active {
  background-color: #a51c311c;
}
.input {
  position: relative;
}
.engine-small.input {
  width: 500px;
}
.not-found {
  position: absolute;
  background-color: rgba(255, 0, 0, 0.6);
  padding: 0.5rem;
  color: white;
  border-radius: 15px;
  font-size: 13px;
  left: 152px;
  top: 230px;
  transition: 300ms;
}
.nearby-color {
  color: rgb(90, 90, 90);
}
.exact-color {
  color: black;
}
</style>
